<template>
  <div
    :class="{
      'role-name-wrapper': true,
      'role-name-wrapper-first': first,
      'role-name-wrapper-editing': editing,
    }"
    :style="{
      width: lane.height + 'px',
      transform: 'rotate(-90deg) translateY(' + -lane.height + 'px)',
      'background-color': laneColor,
      'color': laneTextColor,
    }"
    @mousedown="onHeaderMouseDown"
  >
    <v-tooltip location="top" :key="'move-down-' + index" v-if="!$store.state.isWorkflowDisabled && !last && !disableReorder && !editing" open-delay="500">
      <template v-slot:activator="{ on, props }">
        <div class="move-lane-down-button" @mousedown.stop @click="moveDown(lane.id, index)">
          <button class="captures-description">
            <v-icon v-bind="props" v-on="{ ...on }" black>mdi-chevron-left</v-icon>
          </button>
        </div>
      </template>
      <span>Move the lane down</span>
    </v-tooltip>

    <div
      ref="laneName"
      :id="lane.name"
      :name="lane.name"
      :data-test="svgLanes[lane.id].name"
      class="role-name single-line d-flex justify-center align-center"
      @keydown.stop="checkCharacterLimit($event, 40)"
      @keydown.stop.enter="$event.target.blur()"
      @keydown.stop.esc="restoreValue($event, lane.name)"
      @blur="onNameBlur"
      @paste="onPaste($event, $refs.laneName, 40, true)"
      :contenteditable="editing && !$store.state.isWorkflowDisabled"
      v-text="lane.name"
      @input="() => updateFontSizeDynamically($refs.laneName, true)"
    ></div>

    <v-tooltip location="top" :key="'move-up-' + index" v-if="!$store.state.isWorkflowDisabled && !first && !disableReorder && !editing" open-delay="500">
      <template v-slot:activator="{ on, props }">
        <div class="move-lane-up-button" @mousedown.stop @click="moveUp(lane.id, index)">
          <button class="captures-description">
            <v-icon v-bind="props" v-on="{ ...on }">mdi-chevron-right</v-icon>
          </button>
        </div>
      </template>
      <span>Move the lane up</span>
    </v-tooltip>
  </div>
</template>

<script>
import workflowApi from '@/api/workflowApi'
import { recomputeLaneLayout } from '@/utils/diagramReorder'
import { diagramHelpers } from '@/mixins/diagramHelpers.js'
import { updateFontSizeDynamically } from '@/utils/update-font'
import { selectForegroundColor, getDarkThemeColor } from '@/utils/color'
import { isDarkTheme } from '@/composables/useAppTheme'

const DEFAULT_LANE_COLOR = { red: 207, green: 211, blue: 217 }

export default {
  mixins: [diagramHelpers],
  props: {
    first: {
      type: Boolean,
      default: false
    },
    last: {
      type: Boolean,
      default: false
    },
    lane: Object,
    index: Number,
    svgGraph: Object,
    svgLanes: Object,
    svgGroups: Object,
    workflow: Object,
    disableReorder: Boolean
  },

  data () {
    return {
      previousHeight: null,
      dragCandidate: null,
      editing: false
    }
  },

  computed: {
    isDark () {
      return isDarkTheme()
    },
    usesDefaultColor () {
      const { red, green, blue } = this.workflow.roleDefinition.color

      return red === DEFAULT_LANE_COLOR.red && green === DEFAULT_LANE_COLOR.green && blue === DEFAULT_LANE_COLOR.blue
    },
    laneColor () {
      if (this.usesDefaultColor) {
        return 'var(--diagram-group-header)'
      }
      const rgb = this.workflow.roleDefinition.color.rgb

      return this.isDark ? getDarkThemeColor(rgb) : rgb
    },
    laneTextColor () {
      if (this.usesDefaultColor && this.isDark) {
        return 'var(--text-dark)'
      }
      const color = this.workflow.roleDefinition.color

      return selectForegroundColor(color.red, color.green, color.blue)
    }
  },

  methods: {
    updateFontSizeDynamically,
    onHeaderMouseDown (event) {
      if (this.editing || event.button !== 0 || this.$store.state.isWorkflowDisabled) {
        return
      }
      this.dragCandidate = { x: event.clientX, y: event.clientY, event }
      document.addEventListener('mousemove', this.onHeaderMouseMove)
      document.addEventListener('mouseup', this.onHeaderMouseUp)
    },
    startEditing () {
      if (this.$store.state.isWorkflowDisabled) {
        return
      }
      this.clearDragCandidate()
      this.editing = true
      this.$nextTick(() => {
        const node = this.$refs.laneName
        if (!node) {
          return
        }
        node.focus()
        const selection = window.getSelection && window.getSelection()
        if (!selection) {
          return
        }
        const range = document.createRange()
        range.selectNodeContents(node)
        range.collapse(false)
        selection.removeAllRanges()
        selection.addRange(range)
      })
    },
    onNameBlur (event) {
      this.editing = false
      this.$emit('name-blur', event, this.lane.name, this.index)
    },
    onHeaderMouseMove (event) {
      if (!this.dragCandidate) {
        return
      }
      const moved = Math.abs(event.clientX - this.dragCandidate.x) +
        Math.abs(event.clientY - this.dragCandidate.y)
      if (moved <= 4) {
        return
      }
      const startEvent = this.dragCandidate.event
      this.clearDragCandidate()
      if (this.disableReorder) {
        return
      }
      event.preventDefault()
      const selection = window.getSelection && window.getSelection()
      if (selection && selection.removeAllRanges) {
        selection.removeAllRanges()
      }
      this.$emit('reorder-start', this.lane.id, this.index, startEvent)
    },
    onHeaderMouseUp () {
      if (!this.dragCandidate) {
        return
      }
      this.clearDragCandidate()
      this.startEditing()
    },
    clearDragCandidate () {
      document.removeEventListener('mousemove', this.onHeaderMouseMove)
      document.removeEventListener('mouseup', this.onHeaderMouseUp)
      this.dragCandidate = null
    },
    switchLanes (id1, index1, id2, index2) {
      const orderedIds = Object.values(this.svgLanes)
        .sort((a, b) => a.index - b.index)
        .map((lane) => lane.id)
      orderedIds[index1] = id2
      orderedIds[index2] = id1

      const { svgLanes: newSvgLanes, svgGraph: newSvgGraph } = recomputeLaneLayout(
        this.svgLanes,
        this.svgGraph,
        orderedIds
      )

      workflowApi.updateWorkflow({
        svgGraph: newSvgGraph,
        svgLanes: newSvgLanes,
        svgGroups: this.svgGroups,
        workflow: this.workflow,
        updateNote: {
          action: 'switched lanes',
          target: this.svgLanes[id2].name + ', ' + this.svgLanes[id1].name
        }
      })
    },
    moveUp (laneId, index) {
      if (index === 0) {
        alert('Can\'t move this lane higher up')
        return
      }
      this.switchLanes(laneId, index, this.workflow.lanes[index - 1].id, index - 1)
    },
    moveDown (laneId, index) {
      if (index >= (this.workflow.lanes.length - 1)) {
        alert('Can\'t move this lane further down')
        return
      }
      this.switchLanes(laneId, index, this.workflow.lanes[index + 1].id, index + 1)
    }
  },

  mounted () {
    if (this.$refs.laneName) {
      // Lane height represents the width of text container, because the container is rotated by 90 degrees
      // noinspection JSSuspiciousNameCombination
      this.$refs.laneName.style.width = this.lane.height
      this.previousHeight = this.lane.height
      updateFontSizeDynamically(this.$refs.laneName, true)
    }
    if (this.lane.selectLane) {
      // Newly created lane: drop straight into edit mode.
      this.startEditing()
    }
  },
  beforeUnmount () {
    this.clearDragCandidate()
  },
  watch: {
    'lane.height' (newHeight) {
      if (this.previousHeight !== null && this.previousHeight !== newHeight) {
        if (this.$refs.laneName) {
          this.$refs.laneName.style.width = newHeight
          this.$nextTick(() => {
            updateFontSizeDynamically(this.$refs.laneName, true)
          })
        }
      }
      this.previousHeight = newHeight
    }
  }
}
</script>

<style scoped>
.role-name-wrapper {
  height: 24px;
  /* border-left: 1px solid #A3ADB9; */
  background-color: var(--diagram-group-header);
  display: flex;
  justify-content: center;
  align-items: center;
  transform-origin: right top;
  white-space: nowrap;
}

.role-name-wrapper:hover {
  cursor: grab;
}

.role-name-wrapper:active {
  cursor: grabbing;
}

.role-name-wrapper-editing,
.role-name-wrapper-editing:hover,
.role-name-wrapper-editing:active {
  cursor: text;
}

.role-name-wrapper:hover .move-lane-down-button {
  display: block;
}

.role-name-wrapper:hover .move-lane-up-button {
  display: block;
}

.role-name-wrapper-first {
  border-radius: 0 4px 0 0;
}

.role-name {
  text-align: center;
  overflow: hidden;
  white-space: nowrap;
  word-break: break-word;
  cursor: inherit;
}

[contenteditable="true"].single-line {
  white-space: nowrap;
  overflow: hidden;
}
[contenteditable="true"].single-line br {
  display: none;
}
[contenteditable="true"].single-line * {
  display: inline;
  white-space: nowrap;
}

.move-lane-down-button,
.move-lane-up-button {
  width: 20px;
  position: absolute;
  bottom: 2px;
  display: none;
  background: var(--light-2);
  border-radius: 20%;
}

[data-theme="dark"] .move-lane-down-button,
[data-theme="dark"] .move-lane-up-button {
  color: var(--text-dark);
}

.move-lane-down-button {
  left: 0;
}

.move-lane-up-button {
  right: 0;
}

.v-tooltip__content {
  background: var(--light-1);
  color: var(--color-primary);
  border-radius: 10px;
}

.v-tooltip__content code {
  background: var(--color-primary);
  color: var(--light-1);
}
</style>
